import {
  ShipmentEventType,
  Status,
  TripShipmentLocation,
} from "../../graphql/generated";
import { getEffectiveShipmentIds } from "../shipment/getEffectiveShipmentIds";

export type EtaWindowStatus = "green" | "yellow" | "red";

export type TripShipmentLocationWithEta = TripShipmentLocation & {
  estimatedArrivalTime?: string | Date | null;
};

type ShipmentForVisitCheck = {
  _id: string;
  events?: Array<{
    type: ShipmentEventType | string;
    shipmentLocation?: string | null;
  }> | null;
};

export type TripForShipmentLocationUtils = {
  status: Status;
  shipments: ShipmentForVisitCheck[];
  shipmentLocations: TripShipmentLocationWithEta[];
};

const ARRIVAL_EVENT_TYPES = new Set<ShipmentEventType>([
  ShipmentEventType.PickupCheckpointArrive,
  ShipmentEventType.DropOffCheckpointArrive,
]);

const LIVE_ETA_TRIP_STATUSES = new Set<Status>([
  Status.InProgress,
  Status.Assigned,
]);

export function isTripEligibleForLiveEta(status: Status): boolean {
  return LIVE_ETA_TRIP_STATUSES.has(status);
}

/**
 * True when any linked shipment has a pickup or drop-off checkpoint arrive
 * event for this trip stop.
 */
export function isTripShipmentLocationVisited(
  trip: TripForShipmentLocationUtils,
  location: TripShipmentLocationWithEta,
): boolean {
  const shipmentIds = getEffectiveShipmentIds(location);
  if (shipmentIds.length === 0) {
    return false;
  }

  const locationId = String(location._id);

  return shipmentIds.some((shipmentId) => {
    const shipment = trip.shipments.find((s) => s._id === shipmentId);
    if (!shipment?.events?.length) {
      return false;
    }

    return shipment.events.some(
      (event) =>
        ARRIVAL_EVENT_TYPES.has(event.type as ShipmentEventType) &&
        String(event.shipmentLocation) === locationId,
    );
  });
}

/** Ordered unvisited trip stops. */
export function getRemainingTripShipmentLocations(
  trip: TripForShipmentLocationUtils,
): TripShipmentLocationWithEta[] {
  return trip.shipmentLocations.filter(
    (location) => !isTripShipmentLocationVisited(trip, location),
  );
}

/**
 * True when ETA falls within the first time window. Stops without a time
 * window are treated as on-time.
 */
export function isEtaWithinTimeWindow(
  estimatedArrivalTime: Date,
  location: Pick<TripShipmentLocationWithEta, "timeWindows">,
): boolean {
  const window = location.timeWindows?.[0];
  if (!window) {
    return true;
  }

  const eta = estimatedArrivalTime.getTime();
  return (
    eta >= new Date(window.fromDate).getTime() &&
    eta <= new Date(window.toDate).getTime()
  );
}

/** True when ETA is after the first time window closes. */
export function isEtaAfterTimeWindow(
  estimatedArrivalTime: Date,
  location: Pick<TripShipmentLocationWithEta, "timeWindows">,
): boolean {
  const window = location.timeWindows?.[0];
  if (!window) {
    return false;
  }

  return estimatedArrivalTime.getTime() > new Date(window.toDate).getTime();
}

/** True when ETA is before the first time window opens. */
export function isEtaBeforeTimeWindow(
  estimatedArrivalTime: Date,
  location: Pick<TripShipmentLocationWithEta, "timeWindows">,
): boolean {
  const window = location.timeWindows?.[0];
  if (!window) {
    return false;
  }

  return estimatedArrivalTime.getTime() < new Date(window.fromDate).getTime();
}

/**
 * Color status for a stop's live ETA relative to its time window and later
 * unvisited stops. Returns null for visited stops, missing ETAs, or trips
 * that are not eligible for live ETA display.
 */
export function getEtaWindowStatus(
  trip: TripForShipmentLocationUtils,
  location: TripShipmentLocationWithEta,
): EtaWindowStatus | null {
  if (!isTripEligibleForLiveEta(trip.status)) {
    return null;
  }

  if (isTripShipmentLocationVisited(trip, location)) {
    return null;
  }

  if (!location.estimatedArrivalTime) {
    return null;
  }

  const eta = new Date(location.estimatedArrivalTime);
  if (isEtaAfterTimeWindow(eta, location)) {
    return "red";
  }
  if (isEtaBeforeTimeWindow(eta, location)) {
    return "yellow";
  }

  const remainingStops = getRemainingTripShipmentLocations(trip);
  const locationIndex = remainingStops.findIndex(
    (stop) => stop._id === location._id,
  );
  if (locationIndex === -1) {
    return null;
  }

  const hasLaterLateStop = remainingStops
    .slice(locationIndex + 1)
    .some(
      (stop) =>
        stop.estimatedArrivalTime &&
        isEtaAfterTimeWindow(new Date(stop.estimatedArrivalTime), stop),
    );

  return hasLaterLateStop ? "yellow" : "green";
}
